import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Sparkles } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';
import { createBreadcrumbSchema } from '../utils/seoSchemas';
import useLang from '../hooks/useLang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HistoirePage = () => {
  const { t, i18n } = useTranslation();
  const lang = useLang();
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/timeline?lang=${lang}`);
        setTimeline(response.data.timeline || []);
      } catch (error) {
        console.error('Error fetching timeline:', error);
      }
    };
    fetchTimeline();
  }, [lang, i18n.language]);

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Accueil", path: "/" },
    { name: "Histoire", path: "/histoire" }
  ]);

  const iconMap = {
    calendar: Calendar,
    map: MapPin,
    users: Users,
    sparkles: Sparkles
  };

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="histoire-page">
      <SEO 
        title={seoConfig.histoire.title}
        description={seoConfig.histoire.description}
        keywords={seoConfig.histoire.keywords}
        canonical={seoConfig.histoire.canonical}
        structuredData={breadcrumbSchema}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            {t('history.title')}
          </motion.h1>
          <p className="font-serif italic text-lg text-[#B9B2A8]">
            {t('history.subtitle')}
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#C9A84C] via-[#C9A84C]/50 to-transparent" />

          {timeline.map((event, index) => {
            const IconComponent = iconMap[event.icon] || Calendar;
            return (
              <motion.div
                key={event.id || `timeline-${index}`}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative pl-20 pb-12 last:pb-0"
              >
                <div className="absolute left-0 w-16 h-16 rounded-full bg-[#C9A84C] flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-[#060606]" />
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 card-hover">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-display text-2xl text-[#C9A84C]">{event.year}</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#C9A84C]/50 to-transparent" />
                  </div>
                  <h3 className="font-display text-xl text-[#F5F0E8] mb-2">{event.title}</h3>
                  <p className="text-[#B9B2A8] leading-relaxed">{event.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {timeline.length === 0 && (
          <div className="text-center py-20 text-[#B9B2A8]">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('history.loading')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoirePage;